import React from 'react';
import { Link } from 'react-router-dom';
import '../../../../styles/ai-forge.css';
import * as ROUTES from '../../../constants/routes';
import { AI_FORGE_VARIANTS, AI_FORGE_LOGO, DILIGENTSIA_LOGO, INFO_ICON, KOALENDAR } from './aiForgeData';

/**
 * The three AI Forge Super Flow landing pages — triage, cohort and declined.
 *
 * They are separate Partner records (AFTSF / AFCSF / AFDSF) rather than one record with a URL
 * flag, because `skipInvestorLead` is read off the partner document after registration: triage and
 * cohort skip Section A, declined does not. Three records also mean three referralCodes, so the
 * populations stay separable in reporting.
 *
 * Reason for living outside super-flow/index.js: that file dispatches seven partners through nested
 * `partner.name` ternaries, and these three are distinct layouts rather than copy swaps. Branching
 * once on the key keeps the existing partners untouched.
 *
 * @param {object} partner - the fetched Partner document
 */
const AiForgeSuperFlow = ({ partner }) => {
    const variant = AI_FORGE_VARIANTS[partner?.['key']];
    if (!variant) return null;

    const { eyebrow, eyebrowTone, title, sub, subSmall, primary, secondary, demoLink, footnote, art, section } = variant;
    const registerTo = `${ROUTES.REGISTER}#${partner['_id']}?Superflow`;

    return (
        <div className="dlg-ai-forge">
            <header className="af-header">
                <img className="af-header-logo" src={DILIGENTSIA_LOGO} alt="Diligentsia" />
                <img className="af-header-rec" src={AI_FORGE_LOGO} alt="Recommended for AI Forge Applicants" />
            </header>

            <section className="af-hero">
                <div className="af-hero-copy">
                    <p className={`af-eyebrow${eyebrowTone === 'red' ? ' is-red' : ''}`}>{eyebrow}</p>
                    <h1 className="af-title">{title}</h1>
                    <p className="af-sub">{sub}</p>
                    {subSmall && <p className="af-sub-small">{subSmall}</p>}

                    <div className="af-cta-row">
                        <Link className="af-btn af-btn--primary" to={registerTo}>
                            <span className="af-plus" aria-hidden="true">+</span>
                            <span className="af-btn-label">
                                {primary['small'] && <em>{primary['small']}</em>}
                                {primary['label']}
                            </span>
                        </Link>
                        {secondary['to']
                            ? <Link className="af-btn af-btn--outline" to={secondary['to']}>{secondary['label']}</Link>
                            : <a className="af-btn af-btn--outline" href={secondary['href']} target="_blank" rel="noopener noreferrer">{secondary['label']}</a>}
                    </div>

                    {demoLink && <a className="af-demo-link" href={KOALENDAR} target="_blank" rel="noopener noreferrer">{demoLink}</a>}
                    {footnote && <p className="af-footnote">{footnote}</p>}
                </div>

                {/* One tall panel beside two stacked ones — the same arrangement on all three pages. */}
                <div className="af-hero-art">
                    <img className="af-art-tall" src={art[0]} alt="" />
                    <div className="af-art-stack">
                        <img src={art[1]} alt="" />
                        <img src={art[2]} alt="" />
                    </div>
                </div>
            </section>

            {section === 'oneform' && <OneFormSection {...variant['oneform']} />}
            {section === 'offer' && <OfferSection {...variant['offer']} />}
            {section === 'video' && <VideoSection {...variant['video']} />}
        </div>
    );
};

/** Triage: the OneForm pitch — illustration beside a checklist of what the module returns. */
const OneFormSection = ({ badge, heading, image, bullets }) => (
    <section className="af-band af-oneform">
        <img className="af-oneform-art" src={image} alt="" />
        <div className="af-oneform-copy">
            <span className="af-badge">{'\u2713  '}{badge}</span>
            <h2 className="af-h2">{heading}</h2>
            <ul className="af-list">
                {bullets.map((b) => (
                    <li key={b}><span className="af-tick" aria-hidden="true" />{b}</li>
                ))}
            </ul>
        </div>
    </section>
);

/** Cohort: the three-step offer. */
const OfferSection = ({ heading, cards, footnote }) => (
    <section className="af-band af-offer">
        <h2 className="af-h2 af-offer-head">{heading}</h2>
        <div className="af-offer-grid">
            {cards.map(({ step, title, body, image }) => (
                <article className="af-card" key={step}>
                    <div className="af-card-top">
                        <span className="af-card-step">{step}</span>
                        <img className="af-card-info" src={INFO_ICON} alt="" />
                    </div>
                    <h3 className="af-card-title">{title}</h3>
                    <p className="af-card-body">{body}</p>
                    {/* The asset is the whole footer — bordered mock plus its own pill. */}
                    <img className="af-card-art" src={image} alt="" />
                </article>
            ))}
        </div>
        <p className="af-offer-foot">{footnote}</p>
    </section>
);

/**
 * Declined: the explainer film.
 * The video itself is not finished, so this is the still with its play affordance and no player
 * behind it — swap in an iframe here once a source lands.
 */
const VideoSection = ({ heading, thumbnail }) => (
    <section className="af-band af-video">
        <h2 className="af-h2 af-video-head">{heading}</h2>
        <div className="af-video-frame">
            <img src={thumbnail} alt="" />
            <span className="af-play" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
            </span>
        </div>
    </section>
);

export default AiForgeSuperFlow;
